/**
 * EmployeeOperationsMetrics — one employee's server-side operations metrics (FULL-0128 /
 * PROD-OPS-042).
 *
 * The rendering half of `employeeOperationsMetricsReading.js`. It shows the aggregate
 * `GET /api/agent-runs/stats` answers for one employee over a declared window, beside — not instead
 * of — the existing MONITOR/OPERATE lanes. The lanes answer "what is happening now"; this panel
 * answers "how has this window gone", with the denominator, the analysis provenance and the
 * unavailable-versus-zero distinction stated on screen.
 *
 * Read-only and grants nothing. The panel has no control of its own: the window is fixed by the
 * caller and disclosed, so there is nothing here to click and nothing to mistake for an operation.
 *
 * Failure states are three different sentences:
 *   - refused (403) — a permission answer;
 *   - failed read — the metrics are unavailable, never drawn as zeros;
 *   - no payload — the source did not report the metrics.
 */
import {
  OPERATIONS_METRICS_WINDOW_DAYS,
  OPERATIONS_METRIC_UNKNOWN_HE,
  degradedAnalysesNote,
  formatMetricConfidence,
  formatMetricCount,
  formatMetricDuration,
  formatMetricPercent,
  formatMetricTokens,
  formatMetricUsd,
  hasDegradedAnalyses,
  readOperationsMetrics,
} from "./employeeOperationsMetricsReading";

function MetricTile({ testId, label, value, sub, unknown = false, tone = "text-gray-800" }) {
  return (
    <div
      data-testid={testId}
      data-metric-known={unknown ? "false" : "true"}
      className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 space-y-0.5"
    >
      <div className={`text-lg font-semibold ${unknown ? "text-gray-400" : tone}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

export default function EmployeeOperationsMetrics({
  stats,
  isLoading = false,
  isError = false,
  isRefused = false,
}) {
  if (isRefused) {
    return (
      <p
        role="alert"
        data-testid="operations-metrics-refused"
        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
      >
        אין הרשאה לקרוא את מדדי התפעול של עובד זה
      </p>
    );
  }

  if (isLoading) {
    return (
      <div
        role="status"
        data-testid="operations-metrics-loading"
        className="flex justify-center py-6"
      >
        <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <p
        role="alert"
        data-testid="operations-metrics-failed"
        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
      >
        לא ניתן לטעון את מדדי התפעול — המדדים חסרים ואינם מוצגים כאפס.
      </p>
    );
  }

  const metrics = readOperationsMetrics(stats);
  if (!metrics) {
    return (
      <p
        role="alert"
        data-testid="operations-metrics-not-reported"
        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        המקור לא דיווח על מדדי התפעול — לא ידוע, ולא אפס.
      </p>
    );
  }

  const degradedNote = degradedAnalysesNote(metrics.analysesTotal, metrics.degradedAnalyses);
  const degraded = hasDegradedAnalyses(metrics.analysesTotal, metrics.degradedAnalyses);

  return (
    <section
      aria-label="מדדי תפעול"
      data-testid="operations-metrics"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-sm font-semibold text-gray-700">מדדי תפעול</h2>
        <span className="text-[11px] text-gray-400">
          חלון {OPERATIONS_METRICS_WINDOW_DAYS} הימים האחרונים · חישוב בשרת
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricTile
          testId="operations-metric-total"
          label="ריצות בחלון"
          value={formatMetricCount(metrics.total)}
          unknown={metrics.total == null}
        />
        <MetricTile
          testId="operations-metric-completed"
          label="הושלמו"
          value={formatMetricCount(metrics.completed)}
          unknown={metrics.completed == null}
          tone="text-green-600"
        />
        <MetricTile
          testId="operations-metric-pending"
          label="ממתינות לסקירה"
          value={formatMetricCount(metrics.pendingReview)}
          unknown={metrics.pendingReview == null}
          tone="text-orange-600"
        />
        <MetricTile
          testId="operations-metric-rejected"
          label="נדחו"
          value={formatMetricCount(metrics.rejected)}
          unknown={metrics.rejected == null}
          tone="text-red-600"
        />
        <MetricTile
          testId="operations-metric-success-rate"
          label="שיעור השלמה"
          value={formatMetricPercent(metrics.successRate)}
          unknown={metrics.successRate == null}
          sub={metrics.total === 0 ? "אין ריצות בחלון" : null}
        />
        <MetricTile
          testId="operations-metric-confidence"
          label="ביטחון ממוצע"
          value={formatMetricConfidence(metrics.avgConfidence)}
          unknown={metrics.avgConfidence == null}
        />
        <MetricTile
          testId="operations-metric-duration"
          label="משך ממוצע"
          value={formatMetricDuration(metrics.avgDurationMs)}
          unknown={metrics.avgDurationMs == null}
        />
        <MetricTile
          testId="operations-metric-cost"
          label="עלות מצטברת"
          value={formatMetricUsd(metrics.totalCostUsd)}
          unknown={metrics.totalCostUsd == null}
        />
        <MetricTile
          testId="operations-metric-tokens"
          label="טוקנים (קלט / פלט)"
          value={formatMetricTokens(metrics.inputTokens, metrics.outputTokens)}
          unknown={metrics.inputTokens == null && metrics.outputTokens == null}
        />
        <MetricTile
          testId="operations-metric-analyses"
          label="ניתוחים שנמדדו"
          value={formatMetricCount(metrics.analysesTotal)}
          unknown={metrics.analysesTotal == null}
        />
      </div>

      {degradedNote && (
        <p
          data-testid="operations-metrics-degraded"
          className={`text-[11px] rounded px-2 py-1 border ${
            degraded
              ? "text-amber-800 bg-amber-50 border-amber-200"
              : "text-gray-500 bg-gray-50 border-gray-200"
          }`}
        >
          {degradedNote}
        </p>
      )}

      <p className="text-[11px] text-gray-400">
        המדדים מחושבים בשרת על כל הריצות בחלון, ולא על מדגם הריצות האחרונות שברשימה שלמעלה.
        ערך שלא דווח מוצג כ״{OPERATIONS_METRIC_UNKNOWN_HE}״ ואינו נספר כאפס.
      </p>
    </section>
  );
}
